(() => {
  if (window.TCEDIYCartGroups) return;

  const state = { applying: false, scheduled: false };

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency || ""}`.trim();
    }
  }

  function groupId(item) {
    return item.properties?._DIY_design_group_id || item.properties?.["_DIY design_group_id"] || "";
  }

  function componentId(item) {
    return item.properties?._DIY_component_id || item.properties?.["_DIY component_id"] || "Component";
  }

  function installSequenceToggles(root) {
    root.querySelectorAll(".diy-cart-sequence__toggle").forEach((toggle) => {
      if (toggle.dataset.sequenceToggleReady === "true") return;
      toggle.dataset.sequenceToggleReady = "true";
      toggle.addEventListener("click", (event) => {
        const details = toggle.closest("details.diy-cart-sequence");
        if (!details) return;
        event.preventDefault();
        event.stopPropagation();
        details.open = toggle.classList.contains("diy-cart-sequence__toggle--more");
      });
    });
  }

  function installDetails(row, group) {
    const detailsCell = row.querySelector(".cart-item__details");
    if (!detailsCell) return;
    detailsCell.querySelector(".diy-cart-group__details")?.remove();
    const details = document.createElement("details");
    details.className = "diy-cart-group__details";
    const summary = document.createElement("summary");
    summary.textContent = "View bracelet components";
    const list = document.createElement("ul");
    const components = new Map();
    group.items.forEach(({ item }) => {
      const id = componentId(item);
      const current = components.get(id) || { id, label: item.variant_title || item.title || id, quantity: 0 };
      current.quantity += item.quantity;
      components.set(id, current);
    });
    components.forEach((component) => {
      const entry = document.createElement("li");
      entry.textContent = `${component.label} × ${component.quantity}`;
      list.append(entry);
    });
    details.append(summary, list);
    detailsCell.append(details);
  }

  function installMediaPreview(row, group, drawer) {
    const lead = group.items[0];
    const mediaCell = row.querySelector(".cart-item__media");
    const detailsRoot = drawer ? row : document.getElementById(`CartItem-DiyDetails-${lead.index + 1}`);
    const detailsPreview = row.querySelector(".diy-cart-details .diy-cart-visual")
      || detailsRoot?.querySelector(".diy-cart-details .diy-cart-visual");
    if (!mediaCell || !detailsPreview) return;
    mediaCell.querySelector(".diy-cart-group__media-preview")?.remove();
    const preview = detailsPreview.cloneNode(true);
    preview.classList.add("diy-cart-group__media-preview");
    preview.setAttribute("aria-label", "Custom bracelet preview");
    mediaCell.querySelector(".cart-item__image-container")?.classList.add("diy-cart-group__component-image");
    mediaCell.append(preview);
  }

  function rowFor(index, drawer) {
    return document.getElementById(`${drawer ? "CartDrawer-Item" : "CartItem"}-${index + 1}`);
  }

  function installRemoveButton(row, group, drawer) {
    const cell = row.querySelector(".cart-item__quantity");
    if (!cell) return;
    cell.querySelector("quantity-popover")?.remove();
    cell.querySelector(".cart-item__quantity-wrapper")?.remove();
    let wrap = cell.querySelector(".diy-cart-group__actions");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "diy-cart-group__actions";
      cell.prepend(wrap);
    }
    wrap.innerHTML = "";
    const count = document.createElement("span");
    count.className = "diy-cart-group__count";
    count.textContent = `${group.componentCount} components`;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button button--tertiary diy-cart-group__remove";
    button.textContent = "Remove bracelet";
    button.addEventListener("click", async () => {
      if (button.disabled) return;
      button.disabled = true;
      const updates = Object.fromEntries(group.items.map((entry) => [entry.item.key, 0]));
      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error("Group removal failed");
        window.location.reload();
      } catch (error) {
        button.disabled = false;
        const errorTarget = document.getElementById(drawer ? "CartDrawer-CartErrors" : "cart-errors");
        if (errorTarget) errorTarget.textContent = "Could not remove this bracelet. Please try again.";
      }
    });
    wrap.append(count, button);
  }

  function presentGroup(group, currency, drawer) {
    const lead = group.items[0];
    const leadRow = rowFor(lead.index, drawer);
    if (!leadRow) return;
    leadRow.hidden = false;
    leadRow.classList.add("cart-item--diy-group-lead");
    const title = leadRow.querySelector(".cart-item__name");
    if (title) {
      title.textContent = "Custom Crystal Bracelet";
      title.removeAttribute("href");
    }
    leadRow.querySelectorAll(".cart-item__price-wrapper .price, .cart-item__totals .price").forEach((node) => {
      node.textContent = money(group.totalCents, currency);
    });
    const restoredDetails = leadRow.querySelector(".diy-cart-details");
    leadRow.querySelectorAll(".cart-item__details > .product-option:not(.diy-cart-details)").forEach((node) => node.remove());
    if (!restoredDetails) installDetails(leadRow, group);
    installSequenceToggles(leadRow);
    installMediaPreview(leadRow, group, drawer);
    installRemoveButton(leadRow, group, drawer);

    group.items.slice(1).forEach((entry) => {
      const row = rowFor(entry.index, drawer);
      if (row) row.hidden = true;
      if (!drawer) {
        const details = document.getElementById(`CartItem-DiyDetails-${entry.index + 1}`);
        if (details) details.hidden = true;
      }
    });
  }

  async function apply() {
    if (state.applying) return;
    state.applying = true;
    try {
      const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const cart = await response.json();
      const groups = new Map();
      cart.items.forEach((item, index) => {
        const id = groupId(item);
        if (!id) return;
        const group = groups.get(id) || { id, items: [], totalCents: 0, componentCount: 0 };
        group.items.push({ item, index });
        group.totalCents += item.final_line_price;
        group.componentCount += item.quantity;
        groups.set(id, group);
      });
      groups.forEach((group) => {
        presentGroup(group, cart.currency, false);
        presentGroup(group, cart.currency, true);
      });
      document.documentElement.dataset.diyCartGroupCount = String(groups.size);
    } finally {
      state.applying = false;
    }
  }

  function schedule() {
    if (state.scheduled) return;
    state.scheduled = true;
    requestAnimationFrame(() => {
      state.scheduled = false;
      apply();
    });
  }

  window.TCEDIYCartGroups = { apply, schedule };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", apply, { once: true });
  } else {
    apply();
  }
  document.addEventListener("cart:refresh", schedule);
})();
